import { get } from 'svelte/store';
import { format } from 'svelte-i18n';
import { capFirstChar } from 'src/helpers/utilities';
import { CAMERA_MODE_MOTION_ZONES } from 'src/components/Scenes/helpers/device_conditions/custom_cases/camera/camera-device-helpers';
//Types
import type {
	CameraWebhookMode,
	CameraWebhookSelection,
	WebHooksParams,
	WebHookSupportedList,
} from 'src/types/requests/settings/webhooks';
import type { CameraMotionZone } from 'src/components/Scenes/helpers/device_conditions/custom_cases/camera/camera-device-helpers';

export const CAMERA_WEBHOOK_MOTION_MODE = 'camera_motion';

type FormatFn = (key: string, options?: { default?: string; values?: Record<string, string> }) => string;

const label = (formatFn: FormatFn, key: string, defaultValue: string, values?: Record<string, string>) =>
	capFirstChar(formatFn(key, { default: defaultValue, values }));

export const getCameraWebhookZoneComponent = (zone: string) => `camerazone:${zone.replace('zone_', '')}`;

export const getCameraWebhookZoneName = (zone: string, motionZones: CameraMotionZone[]) => {
	const $format = get(format);
	return (
		motionZones.find((item) => item.value === zone)?.title ||
		zone.replace('zone_', $format('text_zone', { default: 'Zone' }) + ' ')
	);
};

export const getCameraWebhookInitialSelection = (
	actionProps: WebHooksParams,
): CameraWebhookSelection => {
	const selectedMode = actionProps.event?.startsWith('camerazone.')
		? CAMERA_MODE_MOTION_ZONES
		: CAMERA_WEBHOOK_MOTION_MODE;

	return {
		selectedMode,
		selectedZone: selectedMode === CAMERA_MODE_MOTION_ZONES ? `zone_${actionProps.cid}` : '',
		selectedState: actionProps.event?.split('.').pop() || '',
	};
};

export const getCameraWebhookModes = (
	formatFn: FormatFn,
	cameraComponent: string | undefined,
	motionZones: CameraMotionZone[],
	selectedZone: string,
): CameraWebhookMode[] => {
	const zoneName = capFirstChar(getCameraWebhookZoneName(selectedZone, motionZones));

	return [
		{
			id: CAMERA_WEBHOOK_MOTION_MODE,
			tokenId: 'motion',
			title: label(formatFn, 'collapse_title_motion', 'Motion Detection'),
			heading: label(formatFn, 'title_scene_condition_camera_motion', 'When camera motion is'),
			component: cameraComponent,
			states: [
				{
					id: 'motion',
					event: 'camera.motion',
					title: label(formatFn, 'option_camera_motion', 'Motion detected'),
					icon: 'fas fa-person-walking',
				},
				{
					id: 'motion_end',
					event: 'camera.motion_end',
					title: label(formatFn, 'option_camera_motion_end', 'Motion stopped'),
					icon: 'fas fa-person',
				},
			],
		},
		{
			id: CAMERA_MODE_MOTION_ZONES,
			tokenId: 'motion-zones',
			title: label(formatFn, 'camera_motion_zones', 'Motion zones'),
			heading: label(formatFn, 'select_motion_state_for_zone', `Motion state for ${zoneName}`, {
				zoneName,
			}),
			component: selectedZone ? getCameraWebhookZoneComponent(selectedZone) : undefined,
			usesZones: true,
			states: [
				{
					id: 'motion',
					event: 'camerazone.motion',
					title: label(formatFn, 'option_camerazone_motion', 'Motion detected in zone'),
					icon: 'fas fa-person-walking',
				},
				{
					id: 'motion_end',
					event: 'camerazone.motion_end',
					title: label(formatFn, 'option_camerazone_motion_end', 'Motion stopped in zone'),
					icon: 'fas fa-person',
				},
			],
		},
	];
};

export const isCameraWebhookEventSupported = (
	event: string,
	supportedActionsList: WebHookSupportedList,
) => Boolean(supportedActionsList?.types?.[event]);

export const getAvailableCameraWebhookModes = (
	modes: CameraWebhookMode[],
	motionZones: CameraMotionZone[],
	supportedActionsList: WebHookSupportedList,
) =>
	modes.filter(
		(mode) =>
			mode.states.some((state) =>
				isCameraWebhookEventSupported(state.event, supportedActionsList),
			) &&
			(!mode.usesZones || motionZones.length),
	);

export const getAvailableCameraWebhookStates = (
	mode: CameraWebhookMode | undefined,
	supportedActionsList: WebHookSupportedList,
) =>
	mode?.states.filter((state) => isCameraWebhookEventSupported(state.event, supportedActionsList)) ||
	[];
